import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

// Set to true to let Skio control pricing. Default false keeps the theme as the single source of truth, preventing duplicate discounts and price display conflicts.
const SHOW_SKIO_EXTERNAL_PRICE = false

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },
    is_five_pack: { type: Boolean },

    // Per-plan / per-group PDP copy overrides.
    // Shape: { "<plan id | group id | plan name | group name>": { "discount_text": "...", "content": "<html>" }, "default": {...} }
    // Lookup order: plan.id → group.id → plan.name → group.name → "default".
    // IDs are stringified for lookup; keys starting with "_" are ignored (use them for human labels).
    planCopyMap: { type: Object },

    // Allowlist of Skio selling plan IDs this picker is permitted to show, as a
    // comma/newline separated string. Bare IDs and full gid:// URIs both work.
    // Resolved in snippets/skio-block.liquid, in this order:
    //   buy-buttons block setting → section setting → theme setting.
    // Blank means "show every Skio plan" (behaviour before the allowlist existed).
    // See applySellingPlanAllowlist() for the fail-open guard.
    allowedSellingPlanIds: { type: String },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    tooltipContent: { type: String },
    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
        max-width: 440px;
      }

      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 0;
        border: 0;
        font-size: 15px;
        color: black;
        width: 100%;
        margin-bottom: 1rem;
        font-family: inherit;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 10px;
        cursor: pointer;
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;

        gap: 8px;
      }

      .group-container--selected .group-topline {
        font-weight: 500;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }
        .skio_subscription_description_text{
        font-size: 13px;
        margin-left:25px;
        color:#4a4242;
        }
        .skio_onetime_description_text{
        font-size: 13px;
        color:#4a4242;
        }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        align-items: center;
        text-align: right;
        gap: 4px;

        line-height: 1;

        margin-left: auto;
        vertical-align: middle;
            flex-direction: column-reverse;

        font-weight: 500;
      }

      .skio-price s {
        font-size: 13px;

        font-weight: 400;
        opacity: 0.75;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition: max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 25px;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
        order: 2;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 70%;
        line-height: 1.5;
        position: relative;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 0px 8px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        border-radius: 4px;
        font-size: 12px;
        color: var(--skio-discount-text-color, #fff);
        white-space: nowrap;
      }

      .skio-tooltip {
        position: relative;
        display: inline-block;
        cursor: pointer;
        margin-left: 5px;
      }

      .skio-tooltip svg {
        vertical-align: middle;
      }

      .skio-tooltip-text {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        background-color: var(--skio-tooltip-background-color, #333);
        color: var(--skio-tooltip-text-color, #fff);
        padding: 5px 10px;
        border-radius: 5px;
        font-size: 12px;
        white-space: normal;
        width: 200px;
        z-index: 1000;
        transition: opacity 0.3s ease;
      }

      .skio-tooltip:hover .skio-tooltip-text,
      .skio-tooltip:focus-within .skio-tooltip-text {
        visibility: visible;
        opacity: 1;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin-top: 5px;
        background-color: #f7f7f7;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px top 50%;
        background-size: 16px;
        border: 0px solid #ccc;
        border-radius: 2px;
        font-size: 14px;
        color: var(--skio-group-text-selected-color, #000);
        text-overflow: ellipsis;
        white-space: nowrap;
        outline:none !important;
        font-family: inherit;
      }

      .selling-plan-dropdown-label {
        color: #666666;
        font-weight: 500;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }


        .group-content-additional ul{
        margin: 10px 0 0 0 !important;
    padding-left: 1em !important;
    color: #231F20;
    font-family: "Suisse Intl";
    font-size: 15px;
    font-style: normal;
    font-weight: 300;
        }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;

        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;

        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;

        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;

        margin-top: 0;
        margin-bottom: 0;

        letter-spacing: 0;
        line-height: 1.5;

        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;

        width: 2.25em;
        height: 2.25em;

        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;

        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;

        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }
    `,

    /**
     * SECTION: Card layouts
     *
     * Two alternative presentations of the same picker, from the Subscription
     * Widget Figma: 'stack' (white card, accent outline) and 'hero' (accent
     * gradient panel, oversized price). Selected with options.card_layout;
     * `classic` keeps the original list and none of this applies.
     *
     * Colours come from --skio-accent* set on the light-DOM wrapper in
     * snippets/skio-block.liquid — custom properties inherit across the shadow
     * boundary, so the accent is already correct on first paint.
     */
    css`
      :host([card-layout='stack']),
      :host([card-layout='hero']) {
        max-width: 560px;
      }

      /*
        Named container so the hero can go two-column on its own width rather than
        the viewport's — the picker sits in a PDP column that is much narrower than
        the window. Browsers without container queries simply keep the stacked
        layout, which is the correct mobile presentation anyway.
      */
      .sc-picker {
        container: sc-picker / inline-size;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin: 0 0 1rem;
        padding: 0;
        border: 0;
        width: 100%;
        color: #000;
        font-family: inherit;
      }

      .sc-card {
        position: relative;
        border-radius: 16px;
      }

      /* The badge overhangs the top edge, so the card can't clip its own margin box. */
      .sc-card + .sc-card {
        margin-top: 4px;
      }

      .sc-input {
        position: absolute;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }

      .sc-label {
        display: block;
        cursor: pointer;
        border-radius: inherit;
        padding: 18px 20px 18px;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }

      /* Keyboard focus has to be visible even though the radio itself is hidden. */
      .sc-input:focus-visible + .sc-label {
        outline: 2px solid var(--skio-accent, #aac9e9);
        outline-offset: 2px;
      }

      .sc-badge {
        position: absolute;
        top: -13px;
        right: 18px;
        z-index: 1;
        display: inline-flex;
        align-items: center;
        padding: 6px 18px;
        border-radius: 999px;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0;
        text-transform: uppercase;
        white-space: nowrap;
      }

      /*
        Type is straight off the Figma. Suisse Intl ships in exactly two weights
        (300 Light / 500 Medium — see snippets/fonts.liquid), and the design uses
        only those. Do NOT reach for 700 here: the bold cut is a separate family
        ('Suisse Int', no trailing l), so 700 renders as a synthesised faux-bold.

        Where Figma reports 100% line height it means auto — its 30px text sits in
        a 39px box, i.e. the font's natural 1.3. line-height: normal reproduces
        that. Only the benefit copy sets a literal 120%.
      */
      .sc-title {
        font-size: 30px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0;
        margin: 0;
      }

      /*
        Same weight as the title on purpose: this is the discount line, and at 300
        it read as secondary to the plan name when it is the actual offer. The
        one-time card's subtitle is stepped back down two rules below.
      */
      .sc-subtitle {
        font-size: 30px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0;
        margin: 0;
      }

      .sc-price {
        font-size: 30px;
        font-weight: 500;
        line-height: normal;
        text-align: right;
        white-space: nowrap;
      }

      .sc-price s {
        display: block;
        font-size: 30px;
        font-weight: 500;
        color: #c4c4c4;
        text-decoration-thickness: 1.5px;
      }

      /* Frequency: a static pill when the group has one plan, a select when it has several. */
      .sc-frequency {
        margin-top: 12px;
      }

      .sc-frequency-pill {
        display: inline-flex;
        align-items: center;
        padding: 8px 16px;
        border: 1px solid #000;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      .sc-frequency select {
        appearance: none;
        padding: 8px 38px 8px 16px;
        border: 1px solid #000;
        border-radius: 999px;
        background-color: transparent;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 16px center;
        font-family: inherit;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: inherit;
        cursor: pointer;
      }

      .sc-benefits {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
        margin-top: 18px;
        padding: 0;
        list-style: none;
      }

      .sc-benefit {
        display: flex;
        flex-direction: column;
        gap: 7px;
        font-size: 12px;
        font-weight: 300;
        line-height: 1.2;
      }

      .sc-benefit svg {
        display: block;
        width: 26px;
        height: 26px;
        flex: none;
      }

      /*
        Narrow PDP columns can't hold three columns of copy legibly. Going to a
        single column with the icon inline reads better than a 2-wide grid, which
        orphans the third benefit in a half-empty row.
      */
      @media (max-width: 460px) {
        .sc-benefits {
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 20px;
        }

        .sc-benefit {
          flex-direction: row;
          align-items: center;
          gap: 12px;
        }

        .sc-benefit svg {
          width: 22px;
          height: 22px;
        }

        /* The Figma frame is ~500px wide; 30px type doesn't fit a 375px column. */
        .sc-title,
        .sc-subtitle,
        .sc-price,
        .sc-price s {
          font-size: 22px;
        }

        .sc-badge {
          font-size: 13px;
        }
      }

      /*
        The one-time card has no Figma counterpart — the design only draws the
        subscription. At full scale the decline option out-shouts the offer, so it
        renders a step down in the same type system rather than as its own style.
      */
      .sc-card--onetime .sc-title {
        font-size: 20px;
      }

      .sc-card--onetime .sc-subtitle {
        font-size: 14px;
        font-weight: 300;
      }

      .sc-card--onetime .sc-price,
      .sc-card--onetime .sc-price s {
        font-size: 20px;
      }

      /* SECTION: 'stack' — white card with an accent outline (Figma #1) */
      .sc-card--stack .sc-label {
        background: #fff;
        border: 1px solid #e0e0e0;
      }

      .sc-card--stack.sc-card--selected .sc-label {
        border-color: var(--skio-accent, #aac9e9);
        box-shadow: 0 0 0 1px var(--skio-accent, #aac9e9);
      }

      .sc-card--stack .sc-badge {
        background: var(--skio-accent, #aac9e9);
        color: var(--skio-accent-foreground, #000);
      }

      .sc-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
      }

      /*
        SECTION: 'hero' — radio rows (Oct 2026 redesign, replaces the gradient panel)

        Each option is a row: radio, plan copy, price. The selected card carries
        three cues at once, none of them colour-only: a filled radio, a 2px ink
        border, and the product's accent wash. The old panel's only cue was a 1px
        accent border, which nearly vanished against its own gradient.

        The 2px border is a 1px border plus a 1px inset shadow, so selecting a
        card doesn't shift its contents by a pixel.
      */
      .sc-card--hero .sc-label {
        padding: 0;
        border: 1px solid #dedede;
        border-radius: 14px;
        background: #fff;
        transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
      }

      .sc-card--hero .sc-label:hover {
        border-color: #b5b5b5;
      }

      /*
        The tint is mixed here from the raw accent rather than read from
        --skio-accent-wash: the Liquid color_mix in skio-block.liquid can come back
        blank (cappuccino's vibe colour did), and its fallback is the full-strength
        accent, which is far too heavy for a card background.
      */
      .sc-card--hero.sc-card--selected .sc-label {
        border-color: #1c1c1c;
        box-shadow: inset 0 0 0 1px #1c1c1c;
        background: #f4f6f8;
        background: color-mix(in srgb, var(--skio-accent, #aac9e9) 16%, #fff);
      }

      /* Sits over the top edge, lined up with the copy column rather than the radio. */
      .sc-card--hero .sc-badge {
        top: -10px;
        right: auto;
        left: 52px;
        padding: 4px 10px;
        font-size: 10.5px;
        letter-spacing: 0.08em;
        line-height: 1.2;
        background: var(--skio-accent, #aac9e9);
        color: var(--skio-accent-foreground, #000);
      }

      .sc-row {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 14px;
        padding: 18px;
      }

      /* Room for the badge so it doesn't crowd the title. */
      .sc-card--featured .sc-row {
        padding-top: 24px;
      }

      .sc-radio {
        display: grid;
        place-items: center;
        width: 22px;
        height: 22px;
        border: 1.5px solid #9a9a9a;
        border-radius: 50%;
        background: #fff;
        transition: border-color 0.15s ease;
      }

      .sc-radio::after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #1c1c1c;
        transform: scale(0);
        transition: transform 0.15s ease;
      }

      .sc-card--selected .sc-radio {
        border: 2px solid #1c1c1c;
      }

      .sc-card--selected .sc-radio::after {
        transform: scale(1);
      }

      .sc-card--hero .sc-title {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 8px;
        margin: 0;
        font-size: 17px;
        font-weight: 500;
        line-height: 1.25;
      }

      .sc-save {
        padding: 3px 7px;
        border-radius: 4px;
        background: #1c1c1c;
        color: #fff;
        font-size: 10.5px;
        font-weight: 500;
        letter-spacing: 0.06em;
        line-height: 1.2;
        text-transform: uppercase;
      }

      .sc-card--hero .sc-subtitle {
        margin: 3px 0 0;
        font-size: 13px;
        font-weight: 300;
        line-height: 1.35;
        color: rgba(0, 0, 0, 0.72);
      }

      .sc-row-price {
        text-align: right;
        white-space: nowrap;
      }

      /* Roboto Mono, as on the old panel — snippets/fonts.liquid loads the 500 weight. */
      .sc-row-price .sc-amount {
        display: block;
        font-family: 'Roboto Mono', ui-monospace, monospace;
        font-size: 22px;
        font-weight: 500;
        line-height: 1.1;
      }

      .sc-card--hero:not(.sc-card--selected) .sc-amount {
        color: #444;
      }

      /* Plain grey strike rather than the old black pill, which read as a button. */
      .sc-row-price s {
        font-size: 13px;
        color: #9a9a9a;
        text-decoration-thickness: 1px;
      }

      /*
        Everything under the plan row, indented to the copy column. Benefits and
        terms stay visible whether or not the card is selected, so the subscription
        keeps making its case while one-time is picked. Only the delivery control
        depends on selection.
      */
      .sc-extra {
        margin: 0 18px 18px 54px;
        padding-top: 14px;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
      }

      /* Nothing but a collapsed delivery control: hide the divider too. */
      .sc-card:not(.sc-card--selected) .sc-extra--frequency-only {
        display: none;
      }

      .sc-collapse {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.22s ease;
      }

      .sc-collapse > div {
        min-height: 0;
        overflow: hidden;
      }

      .sc-card--selected .sc-collapse {
        grid-template-rows: 1fr;
      }

      .sc-card--hero .sc-frequency {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin: 0;
        padding-bottom: 14px;
        font-size: 13px;
      }

      /* A single plan has nothing to choose, so its name is a plain line, not a control. */
      .sc-card--hero .sc-frequency--static {
        justify-content: flex-start;
        gap: 9px;
      }

      .sc-card--hero .sc-frequency svg {
        flex: none;
        width: 18px;
        height: 18px;
      }

      .sc-card--hero .sc-frequency select {
        padding: 7px 32px 7px 12px;
        border: 1px solid rgba(0, 0, 0, 0.25);
        border-radius: 8px;
        background-color: #fff;
        background-position: right 12px center;
        font-size: 13px;
        letter-spacing: 0;
        text-transform: none;
      }

      .sc-card--hero .sc-benefits {
        grid-template-columns: 1fr;
        gap: 8px;
        margin: 0;
      }

      .sc-card--hero .sc-benefit {
        flex-direction: row;
        align-items: flex-start;
        gap: 9px;
        font-size: 13px;
        line-height: 1.35;
      }

      .sc-card--hero .sc-benefit svg {
        width: 18px;
        height: 18px;
        margin-top: -1px;
      }

      /*
        Campaign terms ("3-month commitment. Then skip or cancel anytime.") get a
        boxed line of their own instead of displacing a benefit, so a commitment is
        stated plainly and the benefit slots stay free. Empty on standard PDPs.
      */
      .sc-terms {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin: 14px 0 0;
        padding: 10px 12px;
        border: 1px solid rgba(0, 0, 0, 0.14);
        border-radius: 10px;
        background: #fff;
        font-size: 13px;
        font-weight: 300;
        line-height: 1.4;
      }

      .sc-extra > .sc-terms:first-child {
        margin-top: 0;
      }

      .sc-terms svg {
        flex: none;
        width: 17px;
        height: 17px;
      }

      .sc-terms strong,
      .sc-terms b {
        font-weight: 500;
      }

      /* Phone-width PDP column: tighter padding, slightly smaller type. */
      @container sc-picker (max-width: 420px) {
        .sc-row {
          gap: 12px;
          padding: 16px 14px;
        }

        .sc-card--featured .sc-row {
          padding-top: 22px;
        }

        .sc-card--hero .sc-title {
          font-size: 16px;
        }

        .sc-row-price .sc-amount {
          font-size: 20px;
        }

        .sc-extra {
          margin: 0 14px 16px 48px;
        }

        .sc-card--hero .sc-badge {
          left: 48px;
        }
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
        this.selectSellingPlan(e.target.value)
      }}
      >
        ${group
        ? this.getAvailableSellingPlans(group).map(
          selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
        )
        : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
        ? this.getAvailableSellingPlans(group).map(
          selling_plan => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan.id}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${e => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}"> ${selling_plan.name} </label>
              `
        )
        : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector && this.options?.dropdownPosition == 'inside' ? 'margin-left' : ''}">
        <span class="selling-plan-dropdown-label">${this.additionalFrequencyLabel()}</span>
        ${this.options?.selector_type == 'dropdown' ? this.sellingPlanDropdown(group) : ''}

        <!-- TODO: Make the label have the border instead of the group -->
        ${this.options?.selector_type == 'button' ? this.sellingPlanButtons(group) : ''} ${this.additionalContentText()}
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it's not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }

  // SECTION: Card layout templates
  // Alternative presentations of the picker — see the card-layout CSS block above.
  // All pricing, discount and selection behaviour is the shared logic; these only
  // change the markup.

  benefitIcon(name) {
    const icons = {
      discount: html`
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M3 11.5A2.5 2.5 0 0 1 5.5 9h21a2.5 2.5 0 0 1 2.5 2.5v2a3 3 0 0 0 0 5v2a2.5 2.5 0 0 1-2.5 2.5h-21A2.5 2.5 0 0 1 3 20.5v-2a3 3 0 0 0 0-5v-2Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linejoin="round"
          />
          <path d="M20 12l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          <circle cx="12.75" cy="12.75" r="1.9" fill="currentColor" />
          <circle cx="19.25" cy="19.25" r="1.9" fill="currentColor" />
        </svg>
      `,
      pause: html`
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="16" cy="16" r="13" fill="currentColor" />
          <path d="M13 11.5v9M19 11.5v9" stroke="#fff" stroke-width="2.5" stroke-linecap="round" />
        </svg>
      `,
      gift: html`
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M4 14h24v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V14Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
          <path d="M2.5 9.5h27V14h-27V9.5ZM16 9.5V29" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
          <path
            d="M16 9.5S14.5 3 11 3a3 3 0 0 0 0 6.5h5Zm0 0S17.5 3 21 3a3 3 0 0 1 0 6.5h-5Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linejoin="round"
          />
        </svg>
      `,
      check: html`
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="16" cy="16" r="13" stroke="currentColor" stroke-width="2" />
          <path d="M10.5 16.5l4 4 7-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      `,
      truck: html`
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M2 7h17v14H2V7ZM19 12h5.5l4.5 4.5V21h-10v-9Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
          <circle cx="8" cy="24" r="2.5" stroke="currentColor" stroke-width="2" />
          <circle cx="23" cy="24" r="2.5" stroke="currentColor" stroke-width="2" />
        </svg>
      `,
    }

    return icons[name] || icons.check
  }

  /**
   * Line icons for the hero rows (24px grid, 1.7 stroke, drawn at 18px). Keyed by
   * the same names the copy uses for `benefitIcon`, so a benefit's chosen icon
   * carries over: `pause` (the "full control" benefit) draws as sliders, which
   * reads as "you're in control" better than a pause symbol does. Anything
   * unknown falls back to an outlined check.
   */
  heroIcon(name) {
    const icons = {
      pause: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
          <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
          <circle cx="15" cy="7" r="2" />
          <circle cx="9" cy="17" r="2" />
        </svg>
      `,
      gift: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">
          <rect x="3.5" y="8.5" width="17" height="4" rx="1" />
          <path d="M5 12.5V20h14v-7.5M12 8.5V20" />
          <path d="M12 8.5S10.8 4 8.5 4a2.2 2.2 0 0 0 0 4.5H12Zm0 0S13.2 4 15.5 4a2.2 2.2 0 0 1 0 4.5H12Z" />
        </svg>
      `,
      truck: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">
          <path d="M2.5 6.5h11v10h-11zM13.5 10h4l3 3.5v3h-7z" />
          <circle cx="6.5" cy="17.5" r="1.8" fill="#fff" />
          <circle cx="17" cy="17.5" r="1.8" fill="#fff" />
        </svg>
      `,
      discount: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">
          <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1 1 0 0 1 0 1.4l-7.3 7.3a1 1 0 0 1-1.4 0z" />
          <circle cx="8" cy="8" r="1.5" />
        </svg>
      `,
      check: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12.3l2.7 2.7L16 9.5" />
        </svg>
      `,
      lock: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">
          <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
          <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
        </svg>
      `,
      repeat: html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 11a7 7 0 0 1 12.5-4.3M20 13a7 7 0 0 1-12.5 4.3" />
          <path d="M17 3v4h-4M7 21v-4h4" />
        </svg>
      `,
    }

    return icons[name] || icons.check
  }

  /**
   * Is this plan's discount the same on every order, or bigger on the first?
   *
   * Read off the plan's own price_adjustments rather than assumed, because the two
   * shapes need different wording: the cans plan is 27% then 10%, the refill-bag
   * plan is a flat 10% forever. Hardcoding the 27%/10% phrasing as the default made
   * every other plan inherit copy that was simply untrue for it.
   *
   * Skio puts the first-order adjustment at position 1 and any later adjustment
   * after it, so "no later adjustment" (or one at the same rate) means flat.
   */
  planDiscountShape(plan) {
    const first = this.discount(plan)?.percent || 0
    const later = this.postCheckoutPriceAdjustments(plan) || []
    const recurring = later.length ? later[later.length - 1].percent : first

    return { first, recurring, isFlat: !later.length || recurring === first }
  }

  /**
   * Copy for one card. Everything is overridable per plan through the shop's
   * `custom.subscription_copy` metafield (see resolvePlanCopy). Without an override
   * the defaults are picked by the plan's discount shape, so a flat-rate plan gets
   * flat-rate wording with no metafield entry and no theme change.
   */
  cardCopy(group) {
    const plan = group?.selected_selling_plan
    const entry = this.resolvePlanCopy(plan) || {}
    const shape = this.planDiscountShape(plan)

    const defaultSubtitle = shape.isFlat ? this.options?.card_title_discount_flat : this.options?.card_title_discount
    const defaultBenefits = shape.isFlat ? this.options?.card_benefits_flat : this.options?.card_benefits

    const title = entry.card_title !== undefined ? entry.card_title : this.options?.card_title
    const subtitle = entry.card_subtitle !== undefined ? entry.card_subtitle : defaultSubtitle

    // `benefits` may be omitted (use the default list) or set to [] to hide the row.
    const benefits = Array.isArray(entry.benefits) ? entry.benefits : defaultBenefits || []

    // Hero layout only. `save_label` is the chip beside the title ("" hides it);
    // `terms` is the campaign commitment line under the benefits ("3-month
    // commitment…"), blank on ordinary plans.
    const save = entry.save_label !== undefined ? entry.save_label : this.options?.card_save_label
    const terms = entry.terms !== undefined ? entry.terms : this.options?.card_terms

    return {
      title: this.replacePlaceholders(title, plan),
      subtitle: this.replacePlaceholders(subtitle, plan),
      // A plan with no discount has nothing to save, so no chip reading "Save 0%".
      save: shape.first > 0 ? this.replacePlaceholders(save, plan) : '',
      terms: this.replacePlaceholders(terms, plan),
      badge: entry.badge,
      benefits: benefits.map(benefit => ({
        icon: benefit.icon,
        text: this.replacePlaceholders(benefit.text, plan),
      })),
    }
  }

  /**
   * Which group wears the "most popular" badge.
   * An explicit `badge` in the plan copy map always wins. Otherwise, when
   * auto_most_popular is on and there is a real choice to make, the badge goes to
   * the group with the largest first-order discount.
   */
  isFeaturedGroup(group) {
    const groups = this.availableSellingPlanGroups || []
    if (groups.length === 0) return false

    const explicit = groups.some(g => (this.resolvePlanCopy(g.selected_selling_plan) || {}).badge)
    if (explicit) return !!(this.resolvePlanCopy(group.selected_selling_plan) || {}).badge

    if (!this.options?.auto_most_popular) return false

    // A single subscription with no one-time alternative isn't a choice, so no badge.
    const showsOnetime = !this.product?.requires_selling_plan && this.options?.show_onetime_card !== false
    if (groups.length === 1 && !showsOnetime) return false

    const best = groups.reduce((winner, candidate) =>
      this.discount(candidate.selected_selling_plan).percent > this.discount(winner.selected_selling_plan).percent ? candidate : winner
    )

    return best === group
  }

  badgeLabel(group) {
    const entry = this.resolvePlanCopy(group?.selected_selling_plan) || {}
    if (entry.badge) return entry.badge
    return this.options?.most_popular_label || 'Most popular'
  }

  /** Frequency control: a static pill for a single plan, a select when there's a choice. */
  cardFrequency(group, layout) {
    const plans = this.getAvailableSellingPlans(group)
    if (!plans.length) return ''

    if (plans.length === 1) {
      /*
       * The single-plan pill defaults to the raw Skio plan name, which is fine on
       * a product page ("Monthly Subscription") but on an offer page whose plan is
       * NAMED after the offer it becomes a third restatement of the deal the
       * subtitle and benefits already made. `frequency_text` in the plan copy map
       * replaces it (placeholders resolve as everywhere else); an explicit empty
       * string hides the pill entirely. The multi-plan <select> below is a
       * functioning control, so copy never overrides it.
       */
      const entry = this.resolvePlanCopy(plans[0]) || {}
      let label = plans[0].name
      if (entry.frequency_text !== undefined) {
        if (!entry.frequency_text) return ''
        label = this.replacePlaceholders(entry.frequency_text, plans[0])
      }
      if (layout === 'hero') {
        return html`
          <div class="sc-frequency sc-frequency--static">
            ${this.heroIcon('repeat')}
            <span>${unsafeHTML(label)}</span>
          </div>
        `
      }
      return html`
        <div class="sc-frequency">
          <span class="sc-frequency-pill">${unsafeHTML(label)}</span>
        </div>
      `
    }

    return html`
      <div class="sc-frequency">
        ${layout === 'hero' ? html`<span aria-hidden="true">Delivery</span>` : ''}
        <label class="sr-only" for="sc-frequency-${this.key}-${group.id}">Select subscription interval</label>
        <select id="sc-frequency-${this.key}-${group.id}" @change=${e => this.selectSellingPlan(e.target.value)}>
          ${plans.map(plan => html`<option ?selected=${group.selected_selling_plan?.id === plan.id} value=${plan.id}>${plan.name}</option>`)}
        </select>
      </div>
    `
  }

  cardBenefits(benefits) {
    if (!benefits?.length) return ''

    return html`
      <ul class="sc-benefits">
        ${benefits.map(
      benefit => html`
            <li class="sc-benefit">
              ${this.benefitIcon(benefit.icon)}
              <span>${unsafeHTML(benefit.text)}</span>
            </li>
          `
    )}
      </ul>
    `
  }

  /**
   * Everything under a hero plan row: the delivery control (shown only while the
   * card is selected), then the benefits and campaign terms (always shown).
   * Each benefit draws the line icon its copy names (see heroIcon).
   */
  heroExtra(group, copy) {
    const frequency = this.cardFrequency(group, 'hero')
    const hasBenefits = copy.benefits?.length > 0
    if (!frequency && !hasBenefits && !copy.terms) return ''

    return html`
      <div class="sc-extra ${!hasBenefits && !copy.terms ? 'sc-extra--frequency-only' : ''}">
        ${frequency ? html`<div class="sc-collapse"><div>${frequency}</div></div>` : ''}
        ${hasBenefits
        ? html`
              <ul class="sc-benefits">
                ${copy.benefits.map(
          benefit => html`
                    <li class="sc-benefit">
                      ${this.heroIcon(benefit.icon)}
                      <span>${unsafeHTML(benefit.text)}</span>
                    </li>
                  `
        )}
              </ul>
            `
        : ''}
        ${copy.terms ? html`<p class="sc-terms">${this.heroIcon('lock')}<span>${unsafeHTML(copy.terms)}</span></p>` : ''}
      </div>
    `
  }

  /**
   * Compare-at price for a subscription card. Mirrors the classic layout's rule:
   * show it when the variant is on sale, or when the plan discounts below the
   * variant price. Returns null when there's nothing to strike through.
   */
  cardComparePrice(group) {
    if (!this.options?.show_compare_price) return null

    const variant = this.selectedVariant
    const discountsBelowVariant = variant.price > this.price(group.selected_selling_plan, false)
    if (!(variant?.compare_at_price > variant.price || discountsBelowVariant)) return null

    return this.money((variant.compare_at_price ? variant.compare_at_price : variant.price) * this.packQuantity)
  }

  onetimeCard(layout) {
    const selected = this.selectedSellingPlanGroup == null
    const comparePrice =
      this.options?.show_compare_price && this.selectedVariant?.compare_at_price > this.selectedVariant.price
        ? this.money(this.selectedVariant.compare_at_price * this.packQuantity)
        : null
    const price = this.money(this.selectedVariant.price * this.packQuantity)

    return html`
      <div class="sc-card sc-card--onetime sc-card--${layout} ${selected ? 'sc-card--selected' : ''}">
        <input
          id="sc-one-time-${this.key}"
          class="sc-input"
          name="skio-group-${this.key}"
          type="radio"
          value="One time purchase"
          ?checked=${selected}
          @change=${() => this.selectSellingPlanGroup(null)}
        />
        <label class="sc-label" for="sc-one-time-${this.key}">
          ${layout === 'hero'
        ? html`
                <div class="sc-row">
                  <span class="sc-radio" aria-hidden="true"></span>
                  <div>
                    <p class="sc-title">${this.options?.card_onetime_title || 'One-time purchase'}</p>
                    ${this.options?.card_onetime_subtitle ? html`<p class="sc-subtitle">${this.options.card_onetime_subtitle}</p>` : ''}
                  </div>
                  <div class="sc-row-price" aria-live="polite">
                    <span class="sc-amount">${price}</span>
                    ${comparePrice ? html`<s><span class="sr-only">was&nbsp;</span>${this.wasLabel(comparePrice)}</s>` : ''}
                  </div>
                </div>
              `
        : html`
                <div class="sc-head">
                  <div>
                    <p class="sc-title">${this.options?.card_onetime_title || 'One-time purchase'}</p>
                    <p class="sc-subtitle">${this.options?.card_onetime_subtitle || ''}</p>
                  </div>
                  <div class="sc-price" aria-live="polite">
                    ${price} ${comparePrice ? html`<s aria-hidden="true">${comparePrice}</s>` : ''}
                  </div>
                </div>
              `}
        </label>
      </div>
    `
  }

  wasLabel(comparePrice) {
    // Struck-through price rather than the word "was": the strike is the <s> at
    // the render sites, and a visually hidden "was" there keeps the meaning for
    // screen readers, which don't announce strikethrough.
    const template = this.options?.was_price_label || '[compare_price]'
    return template.replaceAll('[compare_price]', comparePrice)
  }

  planCard(group, index, layout) {
    const selected = this.selectedSellingPlanGroup == group
    const copy = this.cardCopy(group)
    const featured = this.isFeaturedGroup(group)
    const comparePrice = this.cardComparePrice(group)
    const price = this.price(group.selected_selling_plan)

    return html`
      <div class="sc-card sc-card--${layout} ${selected ? 'sc-card--selected' : ''} ${featured ? 'sc-card--featured' : ''}">
        ${featured ? html`<span class="sc-badge">${this.badgeLabel(group)}</span>` : ''}

        <input
          id="sc-group-${index}-${this.key}"
          class="sc-input"
          name="skio-group-${this.key}"
          type="radio"
          value="${group.id}"
          ?checked=${selected}
          @change=${() => this.selectSellingPlanGroup(group)}
        />

        <label class="sc-label" for="sc-group-${index}-${this.key}">
          ${layout === 'hero'
        ? html`
                <div class="sc-row">
                  <span class="sc-radio" aria-hidden="true"></span>
                  <div>
                    ${copy.title || copy.save
            ? html`<p class="sc-title">
                          ${unsafeHTML(copy.title)} ${copy.save ? html`<span class="sc-save">${unsafeHTML(copy.save)}</span>` : ''}
                        </p>`
            : ''}
                    ${copy.subtitle ? html`<p class="sc-subtitle">${unsafeHTML(copy.subtitle)}</p>` : ''}
                  </div>
                  <div class="sc-row-price" aria-live="polite">
                    <span class="sc-amount">${price}</span>
                    ${comparePrice ? html`<s><span class="sr-only">was&nbsp;</span>${this.wasLabel(comparePrice)}</s>` : ''}
                  </div>
                </div>
                ${this.heroExtra(group, copy)}
              `
        : html`
                <div class="sc-head">
                  <div>
                    ${copy.title ? html`<p class="sc-title">${unsafeHTML(copy.title)}</p>` : ''}
                    ${copy.subtitle ? html`<p class="sc-subtitle">${unsafeHTML(copy.subtitle)}</p>` : ''}
                  </div>
                  <div class="sc-price" aria-live="polite">
                    ${price} ${comparePrice ? html`<s aria-hidden="true">${comparePrice}</s>` : ''}
                  </div>
                </div>
                ${this.cardFrequency(group)}
                ${this.cardBenefits(copy.benefits)}
              `}
        </label>
      </div>
    `
  }

  cardPicker(layout) {
    const showOnetime = !this.product?.requires_selling_plan && this.options?.show_onetime_card !== false

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="sc-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">
          ${unsafeHTML(this.options?.legend_content)}
        </legend>

        ${this.availableSellingPlanGroups?.map((group, index) => this.planCard(group, index, layout))}
        ${showOnetime ? this.onetimeCard(layout) : ''}
      </fieldset>
    `
  }

  // !SECTION: Card layout templates

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return

    // Card layouts from the Subscription Widget Figma. Anything else (including
    // an unset option) keeps the original list.
    const cardLayout = this.options?.card_layout
    if (cardLayout === 'stack' || cardLayout === 'hero') return this.cardPicker(cardLayout)

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">${unsafeHTML(this.options?.legend_content)}</legend>

        ${!this.product.requires_selling_plan
        ? html`
              <div
                class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''} ${!this.options?.onetime_first ? 'group-container--last' : ''}"
                @click=${() => this.selectSellingPlanGroup(null)}
              >
                <input
                  id="one-time-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="One time purchase"
                  ?checked=${!this.selectedSellingPlanGroup}
                  @change=${() => this.selectSellingPlanGroup(null)}
                />

                <label class="group-label" for="one-time-${this.key}">
                  <div class="group-topline">
                    ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                    <div class="group-title">
                    <div> I WANT TO PAY FULL PRICE</div>
                    <div class="skio_onetime_description_text"> ${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}</div>
                   </div>

                    <div class="skio-price" aria-live="polite">
                      ${this.options?.show_compare_price && this.selectedVariant?.compare_at_price > this.selectedVariant.price
                      ? html`<s aria-hidden="true">${this.money(this.selectedVariant.compare_at_price * this.packQuantity)}</s>`
                      : ''}
                        ${this.money(this.selectedVariant.price * this.packQuantity)}
                    </div>
                  </div>
                </label>
              </div>
            `
        : ''}
        ${this.availableSellingPlanGroups?.length
        ? this.availableSellingPlanGroups.map(
          (group, index) => html`
                <div class="group-container ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''}">
                  <input
                    id="group-${index}-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    @change=${() => this.selectSellingPlanGroup(group)}
                    ?checked=${this.selectedSellingPlanGroup == group}
                  />

                  <label class="group-label" for="group-${index}-${this.key}">
                    <div class="group-topline">
                      ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                      <div class="group-title">
                        ${group.name !== 'Prepaid' && this.options?.subscription_title
              ? this.options?.subscription_title
              : group.name == 'Prepaid' && this.options?.prepaid_title
                ? this.options?.prepaid_title
                : group.name}
                        ${this.discountText(group.selected_selling_plan)
              ? html` <span class="savings ${this.options?.discount_style}"> ${this.discountText(group.selected_selling_plan)} </span> `
              : html``}
                        ${this.tooltipContent ? html`<span class="skio-tooltip" tabindex="0" aria-describedby="skio-tooltip-${this.key}-${index}">
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM7 4h2v2H7V4zm0 3h2v5H7V7z"/></svg>
                          <span id="skio-tooltip-${this.key}-${index}" class="skio-tooltip-text" role="tooltip">${this.tooltipContent}</span>
                        </span>` : ''}
                      </div>

                      <div class="skio-price" aria-live="polite">
                        ${this.options?.show_compare_price &&
                          (this.selectedVariant?.compare_at_price > this.selectedVariant.price || this.selectedVariant.price > this.price(group.selected_selling_plan, false))
                          ? html`<s aria-hidden="true">${this.money((this.selectedVariant.compare_at_price ? this.selectedVariant.compare_at_price : this.selectedVariant.price) * this.packQuantity)}</s>`
                          : ''}
                        ${this.price(group.selected_selling_plan)}
                      </div>
                    </div>
                    ${this.subscriptionDescription(group)}

                    ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside' ? this.groupContent(group) : ''}
                  </label>
                </div>
                ${this.selectedSellingPlanGroup == group ? (this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath' ? this.groupContent(group) : '') : ''}
              `
        )
        : ''}
        ${this.options?.show_details == true ? this.showDetails() : ''}
      </fieldset>
    `
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    // First try to find form by specified form_id
    this.form = document.querySelector(`#${this.options?.form_id}`)

    // If no form found by ID, search through parents and page
    if (!this.form) {
      this.form = this.findProductForm()
    }

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput) this.variantInput = document.querySelector(`[name="id"][form="${this.options?.form_id}"]`)

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
      // this.form.addEventListener('change', e => {
      //   this.selectedVariant = this.product.variants.find(variant => variant.id == this.variantInput.value)
      //   this.variantChanged = true
      // })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    let skio = this
    // Example variantChanged event dispatch:
    // document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
    document.addEventListener('variantChanged', function (e) {
      let variantId = e.detail.variantId
      let variant = skio.product.variants.find(x => x.id == variantId)
      if (variant) skio.selectedVariant = variant
      else skio.log('Unable to find variant with id: ', variantId)
      this.variantChanged = true
      skio.requestUpdate()
    })

    // --- SKIO FIX: Listen for the theme's native variant update event ---
    document.addEventListener('variant:update', function (e) {
      if (e.detail && e.detail.resource && e.detail.resource.id) {
        let variantId = e.detail.resource.id;
        let variant = skio.product.variants.find(x => x.id == variantId);
        if (variant) skio.selectedVariant = variant;

        skio.variantChanged = true;
        skio.requestUpdate();

        // The theme's product-price.js updates the DOM immediately on this event.
        // We wait 50 milliseconds for the theme to finish swapping the HTML,
        // then tell SKIO to re-apply the subscription compare-at price formatting.
        setTimeout(() => {
          skio.updateExternalPrice();
        }, 50);
      }
    });
    // --------------------------------------------------------------------

  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))

      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if ((changed.has('selectedVariant') || changed.has('allowedSellingPlanIds')) && this.selectedVariant) {
      const skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      const filteredSellingPlanGroups = this.applySellingPlanAllowlist(skioSellingPlanGroups)

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // TODO: Select proper group depending on what selling plan is selected

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
    this.updateTooltipContent()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // Allowlisted plan IDs as strings, or null when no filtering should happen.
  get allowedPlanIds() {
    if (!this.allowedSellingPlanIds) return null

    const ids = String(this.allowedSellingPlanIds)
      .split(/[^0-9]+/)
      .filter(Boolean)

    return ids.length ? ids : null
  }

  /**
   * Drop the selling plans the template hasn't allowlisted. Filtering happens at
   * the plan level (not the group level) so a new plan added to an existing Skio
   * group can be hidden, and groups left with no plans are dropped entirely.
   *
   * Fail-open guard: if the allowlist matches none of the product's plans, the
   * list is almost certainly meant for a different store — Skio plan IDs are not
   * shared across the US/CA/UK shops — so we show everything rather than silently
   * killing subscriptions on that store. A list matching at least one plan is
   * honoured strictly; that's what keeps a new plan off the existing PDPs.
   */
  applySellingPlanAllowlist(groups) {
    const allowed = this.allowedPlanIds
    if (!allowed) return groups

    const matchesProduct = groups.some(group => group.selling_plans.some(plan => allowed.includes(String(plan.id))))
    if (!matchesProduct) {
      this.log('Selling plan allowlist matched no plans on this product — showing all plans', this.allowedSellingPlanIds)
      return groups
    }

    return groups
      .map(group => ({
        ...group,
        selling_plans: group.selling_plans.filter(plan => allowed.includes(String(plan.id))),
      }))
      .filter(group => group.selling_plans.length > 0)
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateExternalPrice() {
    // When disabled, the theme controls pricing to prevent duplicate compare prices and DOM conflicts with Skio.
    if (!SHOW_SKIO_EXTERNAL_PRICE) return

    const priceContainerSelector = "product-price [ref='priceContainer']"
    document.querySelectorAll(priceContainerSelector).forEach(container => {
      if (this.selectedSellingPlan) {
        // Subscription selected: show discounted price with strikethrough original and save badge
        const subscriptionPrice = this.price(this.selectedSellingPlan)
        const discountPercent = this.discount(this.selectedSellingPlan).percent

        // Use compare_at_price if it exists (permanent sale), otherwise use variant.price as the "original"
        const baseAmount = (this.selectedVariant?.compare_at_price > this.selectedVariant.price)
          ? this.selectedVariant.compare_at_price
          : this.selectedVariant.price
        const originalPriceFormatted = this.money(baseAmount * this.packQuantity)

        container.innerHTML = `
          <span class="price appstle_subscription_final_price">${subscriptionPrice}</span><span class="price" style="text-decoration: line-through;">${originalPriceFormatted}</span>${discountPercent ? `<span class="appstle_subscription_element appstle_subscription_save"> SAVE ${discountPercent}% </span>` : ''}
        `
      } else {
        // One-time purchase: restore original price state
        const regularPrice = this.money(this.selectedVariant.price * this.packQuantity)

        if (this.selectedVariant?.compare_at_price > this.selectedVariant.price) {
          const compareAtPrice = this.money(this.selectedVariant.compare_at_price * this.packQuantity)
          container.innerHTML = `
            <span class="price appstle_subscription_final_price">${regularPrice}</span><span class="price" style="text-decoration: line-through;">${compareAtPrice}</span>
          `
        } else {
          container.innerHTML = `
            <span class="price">${regularPrice}</span>
          `
        }
      }
    })
  }

  additionalFrequencyLabel() {
    if (!this.options?.additional_frequency_label || this.options?.additional_frequency_label == '') return

    return unsafeHTML(this.options?.additional_frequency_label)
  }

  // Resolves the PDP copy entry for a given selling plan from planCopyMap.
  // Lookup order (most specific → least): plan.id → group.id → plan.name → group.name → "default".
  // IDs are stringified because metafield JSON keys are always strings.
  resolvePlanCopy(plan) {
    if (!this.planCopyMap || !plan) return null

    const planId = plan.id != null ? String(plan.id) : null
    const planName = plan.name
    const group = this.availableSellingPlanGroups?.find(g =>
      g.selling_plans?.some(p => p.id === plan.id)
    )
    const groupId = group?.id != null ? String(group.id) : null
    const groupName = group?.name

    return (
      (planId && this.planCopyMap[planId]) ||
      (groupId && this.planCopyMap[groupId]) ||
      (planName && this.planCopyMap[planName]) ||
      (groupName && this.planCopyMap[groupName]) ||
      this.planCopyMap.default ||
      null
    )
  }

  // Renders the gray description line under the discount heading (per group).
  // Sourced from planCopyMap[...].subscription_description; supports [discount] / [future_price_adjustments].
  // If the resolved entry omits the field, falls back to the legacy hardcoded line for backward compat.
  // An explicit empty string suppresses the line entirely.
  subscriptionDescription(group) {
    const plan = group?.selected_selling_plan
    const entry = this.resolvePlanCopy(plan)

    let description
    if (entry && entry.subscription_description !== undefined) {
      description = entry.subscription_description
    } else {
      description = '10% off every subscription order after.'
    }

    if (!description) return ''

    return html`
      <div class="skio_subscription_description_text">
        ${unsafeHTML(this.replacePlaceholders(description, plan))}
      </div>
    `
  }

  additionalContentText() {
    // Resolve copy from the plan copy map; fall back to options.additional_subscription_content.
    // An explicit empty-string content suppresses the bullets block (e.g. refill bag products).
    const copyEntry = this.resolvePlanCopy(this.selectedSellingPlan)
    const defaultContent = this.planDiscountShape(this.selectedSellingPlan).isFlat && this.options?.additional_subscription_content_flat
      ? this.options.additional_subscription_content_flat
      : this.options?.additional_subscription_content
    const content = copyEntry && copyEntry.content !== undefined
      ? copyEntry.content
      : defaultContent

    if (!content || content === '') return

    // Routed through replacePlaceholders so [first_discount] / [recurring_discount]
    // work here too. [discount] and [future_price_adjustments] behave as before.
    return html`
      <div class="group-content-additional">
        ${unsafeHTML(this.replacePlaceholders(content, this.selectedSellingPlan))}
      </div>
    `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return '' // Return empty if any invalid condition is met
    } else {
      // Per-plan/per-group discount_text override from planCopyMap; fall back to
      // options.discount_text, or its flat-rate counterpart when the plan's discount
      // never changes — "27% ON FIRST ORDER" is simply wrong for a flat-10% plan.
      const copyEntry = this.resolvePlanCopy(selling_plan)
      const fallback = this.planDiscountShape(selling_plan).isFlat && this.options?.discount_text_flat
        ? this.options.discount_text_flat
        : this.options?.discount_text
      const template = (copyEntry && copyEntry.discount_text) || fallback

      if (!template) return ''

      return unsafeHTML(this.replacePlaceholders(template, selling_plan))
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage': {
        discount.percent = price_adjustment.value

        /*
         * Derive the amount from the rounded DISCOUNTED UNIT PRICE, because that
         * is what Shopify actually charges. Rounding the discount on its own and
         * subtracting it lands a cent low whenever the discount falls on an exact
         * half cent — 50% of $5.99 is 299.5, which rounded up to a 300 discount
         * and left a 299 unit price. That cent is then multiplied by the pack
         * quantity, so a 50-can subscription displayed $149.50 while the cart
         * charged $150.00.
         *
         * Identical to the old arithmetic for every rate that doesn't hit a half
         * cent (27% of $5.99 gives 162 either way), so this is a fix for the
         * exact-half case rather than a change in pricing behaviour.
         */
        const discountedUnitPrice = Math.round((price * (100.0 - price_adjustment.value)) / 100.0)
        discount.amount = price - discountedUnitPrice
        discount.absolute = discount.amount
        break
      }
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  // !SECTION: Discount Functions

  // Number of cans in the selected pack. Prefers the on-page pack quantity selector (5/20/50); falls back to the legacy is_five_pack flag.
  get packQuantity() {
    const scope = this.closest('.shopify-section') || document
    const checked = scope.querySelector('.pack-qty-fieldset input[type="radio"]:checked')
    if (checked) {
      const qty = parseInt(checked.value, 10)
      if (qty > 0) return qty
    }
    return this.is_five_pack ? 5 : 1
  }

  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }
    return formatted
      ? this.money((this.selectedVariant.price - this.discount(selling_plan).amount) * this.packQuantity)
      : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }

  replacePlaceholders(template, sellingPlan = this.selectedSellingPlan) {
    if (!template) return '';

    const discount = this.discount(sellingPlan);
    const discountText = this.options?.discount_format === 'absolute'
      ? this.money(discount.absolute)
      : discount.percent + '%';

    const futureDiscounts = this.postCheckoutDiscountsText(sellingPlan) || [];
    const futureDiscountsText = futureDiscounts.join(', ');

    // [first_discount] / [recurring_discount] let copy name each rate separately
    // instead of baking "27%" and "10%" into the string. On a flat plan both
    // resolve to the same number. [discount] keeps its existing meaning (first order).
    const shape = this.planDiscountShape(sellingPlan);
    const asText = percent =>
      this.options?.discount_format === 'absolute' ? this.money(Math.round((this.selectedVariant.price * percent) / 100)) : percent + '%';

    return template
      .replaceAll('[discount]', discountText)
      .replaceAll('[first_discount]', asText(shape.first))
      .replaceAll('[recurring_discount]', asText(shape.recurring))
      .replaceAll('[future_price_adjustments]', futureDiscountsText);
  }

  updateTooltipContent() {
    const template = this.options?.tooltip_content || '';
    let plan = this.selectedSellingPlan;
    if (!plan && this.availableSellingPlanGroups?.length) {
      const firstGroup = this.availableSellingPlanGroups[0];
      plan = firstGroup.selected_selling_plan || firstGroup.selling_plans?.[0] || null;
    }
    this.tooltipContent = this.replacePlaceholders(template, plan);
  }

  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker
